import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Paper,
  Typography,
  TextField,
  Divider,
  IconButton,
  Tooltip,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ScheduleSendOutlinedIcon from "@mui/icons-material/ScheduleSendOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";

import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Title,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  FormatColorText,
  Highlight,
} from "@mui/icons-material";

import { useToast } from "../../../shared/toastMessages/toastProvider";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import HighlightExt from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";

import TimeSelector from "../createEmails/features/timeselector";
import MultiSearchSelector from "../createEmails/features/mailingList";
import { theme } from "../../../../configs/theme";
import getApiData from "../../../../hooks/apiRequests/getRequest";

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "15px",
  boxShadow: "1px 5px 5px 1px rgba(0,0,0,0.12)",
  p: { xs: 2, md: 3 },
};

const STATUS_STYLES = {
  sent: { label: "Sent", bgcolor: "#e8f5e9", color: "#2e7d32" },
  pending: { label: "Pending", bgcolor: "#fff3e0", color: "#ef6c00" },
  sending: { label: "Sending", bgcolor: "#fff3e0", color: "#ef6c00" },
  failed: { label: "Failed", bgcolor: "#ffebee", color: "#c62828" },
};

const ToolbarButton = ({ title, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        disabled
        sx={{
          borderRadius: "8px",
          color: "text.disabled",
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const ViewEmail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [title, setTitle] = useState("");
  const [groups, setGroups] = useState([]);
  const [releaseTime, setReleaseTime] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const editor = useEditor({
    editable: false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      Underline,
      Link.configure({ openOnClick: true }),
      Image,
      TextStyle,
      Color,
      HighlightExt,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: "",
  });

  useEffect(() => {
    const loadEmail = async () => {
      try {
        const res = await getApiData(`/Emails/Specific?EmailId=${id}`);
        const data = res?.data;

        if (!data) {
          showToast("Email not found.", "error");
          return;
        }

        setTitle(data.title || "");
        setGroups(
          (data.groups || []).map((group) =>
            typeof group === "object" && group !== null
              ? { id: group.id }
              : { id: group }
          )
        );
        setReleaseTime(data.release_time ? new Date(data.release_time) : null);
        setStatus(data.status || "");

        editor?.commands.setContent(data.body || "");
      } catch (err) {
        console.error(err);
        showToast("Failed to load email.", "error");
      } finally {
        setLoading(false);
      }
    };

    if (editor) loadEmail();
  }, [id, editor, showToast]);

  const handleBack = () => {
    const lastUrl = localStorage.getItem("lastEmailListUrl");
    if (lastUrl) {
      navigate(lastUrl);
      localStorage.removeItem("lastEmailListUrl");
    } else {
      navigate("/Portal/Emails/Manage");
    }
  };

  const statusStyle = STATUS_STYLES[String(status || "").toLowerCase()];
  const isScheduled = Boolean(releaseTime);

  if (loading || !editor) {
    return (
      <Box
        sx={{
          px: { xs: 1, md: 2 },
          py: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary">
          Loading email...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
      <Stack spacing={1} sx={{ py: 2 }}>
        <Button
          variant="text"
          onClick={handleBack}
          startIcon={<ChevronLeftIcon />}
          sx={{
            alignSelf: "flex-start",
            color: "black",
            textTransform: "none",
            fontWeight: 600,
            px: 0,
          }}
        >
          Back
        </Button>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "black" }}>
              View Email
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Read-only preview of this campaign and its delivery settings.
            </Typography>
          </Box>
          {statusStyle ? (
            <Chip
              label={statusStyle.label}
              size="small"
              sx={{
                bgcolor: statusStyle.bgcolor,
                color: statusStyle.color,
                fontWeight: 600,
                borderRadius: "8px",
              }}
            />
          ) : null}
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems="stretch"
      >
        <Paper elevation={0} sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Email title
          </Typography>
          <TextField
            fullWidth
            value={title}
            disabled
            InputProps={{ readOnly: true }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
          />

          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Email content
          </Typography>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "12px",
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
                px: 1,
                py: 0.75,
                bgcolor: "#fafafa",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <ToolbarButton title="Bold (read only)">
                <FormatBold fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Italic (read only)">
                <FormatItalic fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Underline (read only)">
                <FormatUnderlined fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Strikethrough (read only)">
                <StrikethroughS fontSize="small" />
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <ToolbarButton title="Bullet List (read only)">
                <FormatListBulleted fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Numbered List (read only)">
                <FormatListNumbered fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Blockquote (read only)">
                <FormatQuote fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Heading (read only)">
                <Title fontSize="small" />
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <ToolbarButton title="Text Color (read only)">
                <FormatColorText fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Highlight (read only)">
                <Highlight fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Link (read only)">
                <LinkIcon fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Image (read only)">
                <ImageIcon fontSize="small" />
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <ToolbarButton title="Undo (read only)">
                <Undo fontSize="small" />
              </ToolbarButton>
              <ToolbarButton title="Redo (read only)">
                <Redo fontSize="small" />
              </ToolbarButton>
            </Box>

            <Box
              sx={{
                minHeight: 320,
                p: 2,
                bgcolor: "#fafafa",
                "& .tiptap": {
                  minHeight: 280,
                  outline: "none",
                  fontFamily: theme.typography.body1.fontFamily,
                  fontSize: theme.typography.body1.fontSize,
                  lineHeight: 1.6,
                  color: theme.palette.text.primary,
                },
                "& .tiptap p": {
                  margin: "8px 0",
                },
                "& .tiptap h1, & .tiptap h2, & .tiptap h3": {
                  margin: "12px 0 6px",
                  fontWeight: 600,
                },
              }}
            >
              <EditorContent editor={editor} />
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            width: { xs: "100%", lg: 360 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignSelf: { lg: "flex-start" },
            position: { lg: "sticky" },
            top: { lg: 16 },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Email settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Delivery timing and mailing lists for this campaign.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2.5} sx={{ flex: 1 }}>
            <TimeSelector value={releaseTime} disabled />
            <Divider />
            <MultiSearchSelector value={groups} disabled />
          </Stack>

          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{
                py: 1.25,
                px: 2,
                borderRadius: "10px",
                bgcolor: "#fafafa",
                border: "1px solid #eee",
              }}
            >
              {isScheduled ? (
                <ScheduleSendOutlinedIcon fontSize="small" color="action" />
              ) : (
                <SendOutlinedIcon fontSize="small" color="action" />
              )}
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {isScheduled ? "Scheduled campaign" : "Immediate send campaign"}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, textAlign: "center" }}
            >
              This page is read-only. Settings cannot be edited here.
            </Typography>
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};

export default ViewEmail;
